import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CartContext = createContext(null);

const CART_STORAGE_KEY = 'wolkenhelden_cart';

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({
    cloud: null,        // Single cloud configuration
    domains: [],        // Array of domain items
    updatedAt: null
  });
  
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(CART_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setCart(parsed);
      }
    } catch (e) {
      console.error('Error loading cart:', e);
    }
  }, []);

  // Save cart to localStorage on change
  useEffect(() => {
    if (cart.updatedAt) {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
    }
  }, [cart]);

  // Add cloud configuration to cart
  const addCloud = useCallback((cloudConfig) => {
    /*
      cloudConfig = {
        plan: { id, name, ... },
        storage: 100,
        collabora_users: 0,
        antivirus: false,
        hp_backend: false,
        domain: {
          type: 'subdomain' | 'purchase' | 'byod',
          name: 'meinedomain.de',
          price: 4.33 (only for purchase)
        },
        pricing: {
          monthly: 14.99,
          setup: 0,
          domain_yearly: 4.33
        },
        coupon: null | { code, discount_percent }
      }
    */
    setCart(prev => ({
      ...prev,
      cloud: cloudConfig,
      updatedAt: Date.now()
    }));
  }, []);

  // Remove cloud from cart
  const removeCloud = useCallback(() => {
    setCart(prev => ({
      ...prev,
      cloud: null,
      updatedAt: Date.now()
    }));
  }, []);

  // Add domain to cart
  const addDomain = useCallback((domainItem) => {
    /*
      domainItem = {
        id: unique_id,
        domain: 'example.de',
        type: 'registration' | 'transfer',
        price: 4.33,
        years: 1,
        authCode: '' (only for transfer)
      }
    */
    setCart(prev => {
      // Check if domain already exists
      if (prev.domains.find(d => d.domain === domainItem.domain)) {
        return prev;
      }
      return {
        ...prev,
        domains: [...prev.domains, { ...domainItem, id: Date.now() }],
        updatedAt: Date.now()
      };
    });
  }, []);

  // Remove domain from cart
  const removeDomain = useCallback((domainId) => {
    setCart(prev => ({
      ...prev,
      domains: prev.domains.filter(d => d.id !== domainId),
      updatedAt: Date.now()
    }));
  }, []);

  // Check if domain is in cart
  const hasDomain = useCallback((domainName) => {
    return cart.domains.some(d => d.domain === domainName);
  }, [cart.domains]);

  // Clear entire cart
  const clearCart = useCallback(() => {
    setCart({
      cloud: null,
      domains: [],
      updatedAt: Date.now()
    });
    localStorage.removeItem(CART_STORAGE_KEY);
  }, []);

  // Get cart totals
  const getCartTotals = useCallback(() => {
    let initialTotal = 0;      // First payment (setup + first month + domains)
    let monthlyTotal = 0;      // Recurring monthly
    let yearlyDomains = 0;     // Recurring yearly (domains)
    let itemCount = 0;

    // Cloud
    if (cart.cloud) {
      const pricing = cart.cloud.pricing || {};
      initialTotal += (pricing.monthly || 0) + (pricing.setup || 0);
      monthlyTotal += pricing.monthly || 0;
      itemCount++; // Cloud = 1 Artikel
      
      // Domain included with cloud (purchase type) = eigener Artikel
      if (cart.cloud.domain?.type === 'purchase' && cart.cloud.domain?.price) {
        initialTotal += cart.cloud.domain.price;
        yearlyDomains += cart.cloud.domain.price;
        itemCount++; // Kaufdomain = +1 Artikel
      }
    }

    // Standalone domains
    cart.domains.forEach(d => {
      initialTotal += (d.price || 0) * (d.years || 1);
      yearlyDomains += d.price || 0;
      itemCount++;
    });

    return {
      initialTotal,
      monthlyTotal,
      yearlyDomains,
      itemCount,
      hasCloud: !!cart.cloud,
      hasDomains: cart.domains.length > 0
    };
  }, [cart]);

  // Get item count
  const getItemCount = useCallback(() => {
    let count = cart.domains.length;
    if (cart.cloud) {
      count++; // Cloud
      if (cart.cloud.domain?.type === 'purchase' && cart.cloud.domain?.price) {
        count++; // Kaufdomain
      }
    }
    return count;
  }, [cart]);

  const value = {
    cart,
    isCartOpen,
    setIsCartOpen,
    addCloud,
    removeCloud,
    addDomain,
    removeDomain,
    hasDomain,
    clearCart,
    getCartTotals,
    getItemCount
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;
